import { createTheme } from '@mui/material/styles';
import tokens from './tokens';

// createTheme wrapper around the shared design tokens. Components
// overrides align MUI defaults to our brand: amber primary, rounded
// controls, no all-caps buttons, Noto Sans throughout.
function buildTheme({ mode }) {
    const isDark = mode === 'dark';

    return createTheme({
        palette: {
            mode,
            primary: {
                main:  tokens.color.amber,
                dark:  tokens.color.amberDark,
                light: tokens.color.amberTint,
                contrastText: tokens.color.ink,
            },
            secondary: {
                main:  tokens.color.ink,
                dark:  tokens.color.inkStrong,
                light: tokens.color.inkMuted,
                contrastText: tokens.color.bg,
            },
            success: { main: tokens.color.success },
            warning: { main: tokens.color.warning },
            error:   { main: tokens.color.error },
            info:    { main: tokens.color.info },
            background: {
                default: isDark ? tokens.color.dark1 : tokens.color.bg,
                paper:   isDark ? tokens.color.dark2 : tokens.color.bg,
            },
            text: {
                primary:   isDark ? tokens.color.onDark     : tokens.color.ink,
                secondary: isDark ? tokens.color.onDarkSoft : tokens.color.inkMuted,
                disabled:  isDark ? tokens.color.onDarkMute : tokens.color.inkSoft,
            },
            divider: isDark ? tokens.color.dark3 : tokens.color.line,
        },
        shape: {
            borderRadius: 12,
        },
        typography: {
            fontFamily: tokens.fontFamily,
            h1: { fontSize: 32, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15 },
            h2: { fontSize: 26, fontWeight: 800, letterSpacing: '-0.015em', lineHeight: 1.2 },
            h3: { fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.25 },
            h4: { fontSize: 18, fontWeight: 700, lineHeight: 1.3 },
            h5: { fontSize: 16, fontWeight: 700, lineHeight: 1.35 },
            h6: { fontSize: 14, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' },
            body1: { fontSize: 15, lineHeight: 1.55 },
            body2: { fontSize: 14, lineHeight: 1.55 },
            button: { textTransform: 'none', fontWeight: 700, letterSpacing: '0.01em' },
        },
        components: {
            MuiButton: {
                defaultProps: {
                    disableElevation: true,
                },
                styleOverrides: {
                    root: {
                        borderRadius: tokens.radius.sm,
                        paddingTop: 10,
                        paddingBottom: 10,
                        paddingInline: 18,
                    },
                    containedPrimary: {
                        boxShadow: tokens.shadow.amber,
                        '&:hover': { boxShadow: tokens.shadow.amber, filter: 'brightness(1.02)' },
                    },
                    sizeLarge: { paddingTop: 13, paddingBottom: 13, fontSize: 15 },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: { backgroundImage: 'none' },
                    rounded: { borderRadius: tokens.radius.md },
                    elevation1: { boxShadow: tokens.shadow.sm },
                    elevation3: { boxShadow: tokens.shadow.md },
                },
            },
            MuiTextField: {
                defaultProps: { variant: 'outlined', size: 'medium' },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: tokens.radius.sm,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: tokens.color.amberDark,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: 2,
                        },
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: { borderRadius: tokens.radius.sm },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: { fontWeight: 700, letterSpacing: '0.02em' },
                },
            },
            MuiAppBar: {
                defaultProps: { elevation: 0, color: 'default' },
            },
            MuiLink: {
                defaultProps: { underline: 'hover' },
            },
        },
    });
}

export const lightTheme = buildTheme({ mode: 'light' });
export const darkTheme  = buildTheme({ mode: 'dark' });

export default lightTheme;
